import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Modal, Button, CircularProgress, Snackbar, Alert,
    List, ListItem, ListItemText, ListItemIcon, Switch, IconButton, Divider
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DashboardService } from '../service';

const WIDGET_LABELS = {
    occupancy: 'Occupancy',
    revenue: "Today's Revenue",
    roomStatus: 'Room Inventory',
    staffOnDuty: 'Staff & Shifts',
    weeklyChart: 'Performance Analytics',
    revenueBreakdown: 'Revenue Channels',
    nightAudit: 'Night Audit Engine',
    alerts: 'Alerts',
    activityLog: 'Activity'
};

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: 520 },
    maxHeight: '80vh',
    bgcolor: 'background.paper',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    borderRadius: 3,
    outline: 'none',
    display: 'flex',
    flexDirection: 'column'
};

export default function CustomiseDashboardModal({ open, onClose, widgetOrder, hiddenWidgets, tenantId: propTenantId, onSave }) {
    const [order, setOrder] = useState([]);
    const [hidden, setHidden] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'success' });

    // Sync local state whenever the modal opens, strictly filtering out unwanted options
    useEffect(() => {
        if (open) {
            const allowed = Object.keys(WIDGET_LABELS);
            setOrder((widgetOrder || []).filter(id => allowed.includes(id)));
            setHidden((hiddenWidgets || []).filter(id => allowed.includes(id)));
        }
    }, [open, widgetOrder, hiddenWidgets]);

    const moveWidget = (index, direction) => {
        const newOrder = [...order];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newOrder.length) return;
        [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
        setOrder(newOrder);
    };

    const toggleVisibility = (widgetId) => {
        setHidden(prev =>
            prev.includes(widgetId)
                ? prev.filter(id => id !== widgetId)
                : [...prev, widgetId]
        );
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = propTenantId || authData.tenantId || '1';

            await DashboardService.savePreferences(tenantId, {
                v2: true,
                widgetOrder: order,
                hiddenWidgets: hidden
            });

            setAlertInfo({
                open: true,
                message: 'Dashboard layout preferences saved successfully',
                severity: 'success'
            });

            setTimeout(() => {
                onSave(order, hidden);
                onClose();
            }, 1000);
        } catch (err) {
            console.error('Failed to save dashboard preferences:', err);
            setAlertInfo({
                open: true,
                message: err.message || 'Failed to save layout preferences. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={open} onClose={isSaving ? null : onClose}>
                <Box sx={modalStyle}>
                    {/* Header */}
                    <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                            Customise Dashboard
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                            Reorder widgets and toggle their visibility. Changes are saved to your profile.
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Widget List */}
                    <Box sx={{ overflowY: 'auto', flex: 1, px: 1 }}>
                        <List disablePadding>
                            {order.map((widgetId, idx) => {
                                const isHidden = hidden.includes(widgetId);
                                const label = WIDGET_LABELS[widgetId] || widgetId;

                                return (
                                    <ListItem
                                        key={widgetId}
                                        sx={{
                                            borderRadius: 2,
                                            mb: 0.5,
                                            bgcolor: isHidden ? 'action.disabledBackground' : 'transparent',
                                            opacity: isHidden ? 0.6 : 1,
                                            transition: 'all 0.2s ease'
                                        }}
                                        secondaryAction={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    disabled={idx === 0}
                                                    onClick={() => moveWidget(idx, -1)}
                                                >
                                                    <ArrowUpwardIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    disabled={idx === order.length - 1}
                                                    onClick={() => moveWidget(idx, 1)}
                                                >
                                                    <ArrowDownwardIcon fontSize="small" />
                                                </IconButton>
                                                <Switch
                                                    edge="end"
                                                    size="small"
                                                    checked={!isHidden}
                                                    onChange={() => toggleVisibility(widgetId)}
                                                    color="primary"
                                                />
                                            </Box>
                                        }
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <DragIndicatorIcon fontSize="small" color="disabled" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={label}
                                            secondary={isHidden ? 'Hidden' : `Position ${idx + 1}`}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                fontWeight: isHidden ? 'regular' : 'medium',
                                                sx: { textDecoration: isHidden ? 'line-through' : 'none' }
                                            }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                            sx={{ pr: 14 }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>

                    <Divider />

                    {/* Footer Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 3, py: 2 }}>
                        <Button
                            onClick={onClose}
                            disabled={isSaving}
                            variant="text"
                            sx={{ textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="primary"
                            disabled={isSaving}
                            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
                        >
                            Save Layout
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={alertInfo.open}
                autoHideDuration={4000}
                onClose={() => setAlertInfo({ ...alertInfo, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setAlertInfo({ ...alertInfo, open: false })}
                    severity={alertInfo.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alertInfo.message}
                </Alert>
            </Snackbar>
        </>
    );
}
